import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import momentTZ from "moment-timezone";
import { useFormContext } from "react-hook-form";

import TimeInput from "components/atoms/TimeInput/TimeInput";
import Alert from "components/molecules/Alert/Alert";
import FormHr from "pages/shipper/RequestForm/components/Common/FormHr";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import { formatScheduleTime } from "helpers/multistopHelpers";
import StopInfo from "./StopInfo";

interface IStopScheduleFormProps {
  quoteRequestDetails: IQuotationData;
  countries: ICountries[];
}

const getDefaultDateTime = (stop: any) => {
  let sourceDatetime: string | undefined;

  switch (stop.stop_time_type) {
    case "After":
      sourceDatetime = stop.stop_after_datetime;
      break;
    case "Before":
    case "Between":
      sourceDatetime = stop.stop_before_datetime;
      break;
    default:
      return { date: undefined, time: undefined };
  }

  if (!sourceDatetime || !stop.stop_timezone) {
    return { date: undefined, time: undefined };
  }

  const m = momentTZ(sourceDatetime).tz(stop.stop_timezone);
  return {
    date: m.format("YYYY-MM-DD"),
    time: m.format("HH:mm:ss"),
  };
};

export default function StopScheduleForm({ quoteRequestDetails, countries }: IStopScheduleFormProps) {
  const intl = useIntl();
  const stops = quoteRequestDetails?.schedule?.stops || [];
  const [holidays, setHolidays] = useState({});
  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const earliestPickup = useMemo(() => {
    const pickupEntries = stops
      .filter((s: any) => s.stop_type === "Pickup" && s.stop_after_datetime && s.stop_timezone)
      .map((s: any) => ({ datetime: momentTZ(s.stop_after_datetime).tz(s.stop_timezone), timezone: s.stop_timezone }));

    if (!pickupEntries.length) return null;

    return pickupEntries.reduce((earliest, current) => (current.datetime.isBefore(earliest.datetime) ? current : earliest));
  }, [stops]);

  const formattedEarliestPickup = useMemo(
    () => earliestPickup?.datetime.format("DD MMMM YYYY. HH:mm") ?? "",
    [earliestPickup],
  );

  const checkValidQuotedDate = useCallback(
    (index: number) => {
      if (!earliestPickup) return true;
      const quotedDate = getValues(`schedule.stops[${index}].quoted_date`);
      const quotedTime = getValues(`schedule.stops[${index}].quoted_time`) || "00:00:00";
      const stopTz = stops[index]?.stop_timezone;
      if (!quotedDate || !stopTz) return true;
      const quotedMoment = momentTZ.tz(`${quotedDate} ${quotedTime}`, "YYYY-MM-DD HH:mm:ss", stopTz);
      if (!quotedMoment.isValid()) return true;
      return moment(earliestPickup.datetime).isBefore(moment(quotedMoment));
    },
    [earliestPickup, getValues, stops],
  );

  useEffect(() => {
    stops.forEach((stop: any, index: number) => {
      const currentDate = getValues(`schedule.stops[${index}].quoted_date`);
      const currentTime = getValues(`schedule.stops[${index}].quoted_time`);
      const { date: defaultDate, time: defaultTime } = getDefaultDateTime(stop);
      if (!currentDate) setValue(`schedule.stops[${index}].quoted_date`, defaultDate);
      if (!currentTime) setValue(`schedule.stops[${index}].quoted_time`, defaultTime);
    });
    stops.forEach((stop: any, index: number) => {
      setValue(`schedule.stops[${index}].stop_id`, stop.stop_id ?? "");
    });
  }, [getValues, quoteRequestDetails, setValue, stops]);

  return (
    <div className="stop-schedule-form">
      {stops.map((stop: any, index: number) => {
        const isPickup = stop.stop_type === "Pickup";
        const currentDate = getValues(`schedule.stops[${index}].quoted_date`);
        const currentTime = getValues(`schedule.stops[${index}].quoted_time`);
        const { date: defaultDate, time: defaultTime } = getDefaultDateTime(stop);

        return (
          <div key={index}>
            <div className="flex-row mb-24">
              <div className="w-300 pr-16">
                <StopInfo stop={stop} />
              </div>

              <div>
                <div>
                  <label className="required">
                    {isPickup ? (
                      <FormattedMessage id="quote.provider.latestPickupTime" defaultMessage="Latest pickup time" />
                    ) : (
                      <FormattedMessage id="quote.provider.estimatedDeliveryTime" defaultMessage="Estimated delivery time" />
                    )}
                  </label>
                  <div className="d-flex gap-xs font-b3 text-secondary mb-2">
                    <FormattedMessage id="general.requested" defaultMessage="Requested" />
                    {": "}
                    {formatScheduleTime({
                      stop_after_datetime: stop.stop_after_datetime,
                      stop_before_datetime: stop.stop_before_datetime,
                      stop_time_type: stop.stop_time_type,
                      stop_timezone: stop.stop_timezone,
                      excludeTimezone: true,
                      intl,
                    })}
                  </div>
                </div>

                <div className="flex-row">
                  <div className="w-192 align-date">
                    <PickupDeliveryDate
                      datePickerProps={{
                        name: `schedule.stops[${index}].quoted_date`,
                        required: true,
                        defaultValue: currentDate ?? defaultDate,
                        ...(!isPickup &&
                          earliestPickup && {
                            validations: {
                              isValidDeliveryDate: () => checkValidQuotedDate(index),
                            },
                            hideValidationError: true,
                          }),
                      }}
                      countries={countries}
                      holidays={holidays}
                      setHolidays={setHolidays}
                    />
                  </div>

                  <TimeInput
                    name={`schedule.stops[${index}].quoted_time`}
                    required
                    defaultValue={currentTime ?? defaultTime}
                    {...(!isPickup && {
                      registerOptions: {
                        deps: [`schedule.stops[${index}].quoted_date`],
                      },
                    })}
                  />
                </div>

                {!isPickup && (errors as any)?.schedule?.stops?.[index]?.quoted_date && (
                  <>
                    {(errors as any).schedule.stops[index].quoted_date.type === "required" ? (
                      <div className="d-block font-b3 invalid-feedback">
                        <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
                      </div>
                    ) : (
                      <Alert className="w-100 mt-md" variant="danger">
                        <FormattedMessage
                          id="new-request.schedule.multistop.error.deliveryAfterPickup"
                          defaultMessage="Delivery schedule should be after {pickupDateTime} (note: timezone is considered as well)"
                          values={{ pickupDateTime: formattedEarliestPickup }}
                        />
                      </Alert>
                    )}
                  </>
                )}
              </div>
            </div>

            <FormHr />
          </div>
        );
      })}
    </div>
  );
}
